import forge from 'apis/forge';
import { AuthUser } from 'dragonmouse-shared/interfaces';
import { isEqual } from 'lodash';
import {
	refreshFramePermissions,
	setLoadingState,
	setSceneLoadError,
	updateChats,
	updateFrames,
	updateGuestFrames,
	updateGuestScenes,
	updateScenes,
	updateTrackedUsers,
	updateUser,
	updateWallet,
} from 'redux/db/actions';
import store from 'redux/store';
import { validate as validateUuid } from 'uuid';
import { ActiveSceneFramesStream } from './stream/ActiveSceneFramesStream';
import { GuestFramesStream } from './stream/GuestFramesStream';
import { GuestScenesStream } from './stream/GuestScenesStream';
import { SceneIdsStream } from './stream/SceneIdsStream';
import { ScenesStream } from './stream/ScenesStream';
import { TrackedUserStream } from './stream/TrackedUserStream';
import { ChatPollingStream } from '../polling/ChatPollingStream';

class ElectricSync {
	// Data to track - currently matches the Redux store
	private _authUserId: string | null = null;
	private _activeSceneId: string | null = null;
	private _trackedUserIds: string[] | null = null;

	// Syncing a user's scenes (the ones they can edit) is intricate.
	// I wish it could be easy. After all, the server has in the DB a list of a user's scenes
	// Why can't we simply subscribe?
	//  - Electric doesn't support joins. only basic 'where' clauses are supported. So we can't pass
	//    in a userId and say to electric "look up this UserID in scene_users, join with the scenes table
	//    to get the scenes they can edit, and let me know when that changes"
	// This leads to a two-fold solution
	//     - one stream to get the list of sceneIds (based on userId)
	//     - one stream to get the scenes (based on sceneIds)
	// This solution worked for us for a while! But we want to get away from it.
	// The list of sceneIds is long, could be too long to fit into an electric URL.
	// So our current solution is like so:
	//     - one stream to get the list of sceneIds (based on userId)
	//     - one stream to get the scenes (based on userId as well)
	// The thinking is that, if the first stream has gotten an update from electric, well certainly the
	// second stream should be restarted and give us new data.
	// Note the second stream will push updates to the scene themselves (it is doing a 'where' clause on the
	// list of sceneIds the user has when the stream is started)
	private _sceneIds: Set<string> = new Set();

	// ** 1 **
	// streams are based on authUserId
	private _sceneIdsStream: SceneIdsStream | null = null;
	private _guestScenesStream: GuestScenesStream | null = null;
	private _guestFramesStream: GuestFramesStream | null = null;

	// ** 2 **
	// stream based on sceneIds
	private _scenesStream: ScenesStream | null = null;

	// ** 3 **
	// streams based on activeSceneId
	private _activeSceneFramesStream: ActiveSceneFramesStream | null = null;
	private _activeSceneChatsStream: ChatPollingStream | null = null;

	// ** 4 **
	// stream based on trackedUserIds
	private _trackedUserStream: TrackedUserStream | null = null;

	constructor() {
		// These are values in the Redux store that we need to track.
		// For example when the activeSceneId changes from null to a scene ID, we
		// will start streaming the frames and chats and storing them in Redux.
		// Conversely, when the activeSceneId changes to null, we
		// will stop streaming the frames and chats and remove them from Redux.
		store.subscribe(() => {
			this.authUser = store.getState().mode.authUser;
			this.activeSceneId = store.getState().mode.activeSceneId;
			this.trackedUserIds = store.getState().mode.trackedUserIds;
		});
	}

	// ** 1 **
	set authUser(authUser: AuthUser | null) {
		if (authUser) {
			if (authUser.id !== this._authUserId) {
				this._authUserId = authUser.id;
				this.onAuthUserIdChanged(authUser?.id ?? null);
			}
		} else {
			if (this._authUserId) {
				this._authUserId = null;
				this.onAuthUserIdChanged(null);
			}
		}
	}

	// ** 2 **
	set sceneIds(sceneIds: Set<string>) {
		if (!isEqual(this._sceneIds, sceneIds)) {
			this._sceneIds = sceneIds;
			this.onSceneIdsChanged(sceneIds);
		}
	}

	// ** 3 **
	set activeSceneId(id: string | null) {
		if (id !== this._activeSceneId) {
			this._activeSceneId = id;
			this.onActiveSceneIdChanged(id);
		}
	}

	// ** 4 **
	set trackedUserIds(ids: string[]) {
		if (!isEqual(this._trackedUserIds, ids)) {
			this._trackedUserIds = ids;
			this.onTrackedUserIdsChanged(ids);
		}
	}

	// ** 1 **
	onAuthUserIdChanged(userId: string | null) {
		this._sceneIdsStream?.stop('onAuthUserIdChanged');
		this._guestScenesStream?.stop('onAuthUserIdChanged');
		this._guestFramesStream?.stop('onAuthUserIdChanged');

		if (userId) {
			// Fetch user data (no longer streaming)
			store.dispatch(setLoadingState({ resource: 'user', state: 'loading' }));
			forge
				.fetchUser()
				.then((user: any) => {
					store.dispatch(setLoadingState({ resource: 'user', state: 'loaded' }));

					const isGuest = user?.id === 'guest';

					// Only fetch wallet and scenes for non-guest users
					if (!isGuest) {
						forge.fetchWallet().catch((error: any) => {
							console.error('Failed to fetch wallet:', error);
							store.dispatch(updateWallet(null));
						});

						// user scenes (scenes they can edit)
						this._sceneIdsStream = new SceneIdsStream(userId, (sceneIds) => {
							// if the list of sceneIds is empty, we consider the scenes to have been initially loaded
							// this may seem strange but, in our system, there is a big difference between "never-loaded" and "loaded"
							// (when the former is set some things do not intiialize correctly)
							if (sceneIds.size === 0) {
								store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));
							}
							this.sceneIds = sceneIds;
						});
						this._sceneIdsStream.start();
					} else {
						// Guest users don't have editable scenes, mark as loaded immediately
						store.dispatch(setLoadingState({ resource: 'sceneIds', state: 'loaded' }));
						store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));
						store.dispatch(updateWallet(null));
					}
				})
				.catch((error: any) => {
					console.error('Failed to fetch user:', error);
					store.dispatch(updateUser(null));
					store.dispatch(setLoadingState({ resource: 'user', state: 'loaded' }));
				});

			// guest scenes (scenes they can view)
			// (this list should only change when page is refreshed, which should also cause userId to change and this to be called again)
			this._guestScenesStream = new GuestScenesStream(userId);
			this._guestScenesStream.start();

			// // guest frames (frames they can view)
			// // (this list should only change when page is refreshed, which should also cause userId to change and this to be called again)
			this._guestFramesStream = new GuestFramesStream(userId);
			this._guestFramesStream.start();
		} else {
			// clear old scenes and frames -- user Id is null so we should not have access to them
			store.dispatch(updateScenes([]));
			store.dispatch(updateGuestScenes([]));
			store.dispatch(updateGuestFrames([]));
			store.dispatch(updateFrames([]));
			store.dispatch(updateChats([]));
			store.dispatch(updateTrackedUsers([]));
			this._sceneIds.clear();
		}
	}

	// ** 2 **
	onSceneIdsChanged(sceneIds: Set<string>) {
		this._scenesStream?.stop('onSceneIdsChanged');

		if (sceneIds.size > 0 && this._authUserId) {
			// set loading flag?
			this._scenesStream = new ScenesStream(this._authUserId);
			this._scenesStream.start();
		} else {
			!this._authUserId && console.error('onSceneIdsChanged: no authUserId');

			store.dispatch(updateScenes([]));
			store.dispatch(refreshFramePermissions()); // frame permissions change when scenes are cleared
		}
	}

	// ** 3 **
	onActiveSceneIdChanged(activeSceneId: string | null) {
		this._activeSceneFramesStream?.stop('onActiveSceneIdChanged');
		this._activeSceneChatsStream?.stop('onActiveSceneIdChanged');
		store.dispatch(setSceneLoadError(null));

		if (activeSceneId) {
			if (!validateUuid(activeSceneId)) {
				store.dispatch(setSceneLoadError('Invalid scene ID'));
				store.dispatch(updateFrames([]));
				store.dispatch(updateChats([]));
				return;
			}

			this._activeSceneFramesStream = new ActiveSceneFramesStream(activeSceneId);
			this._activeSceneChatsStream = new ChatPollingStream(activeSceneId);
			this._activeSceneFramesStream.start();
			this._activeSceneChatsStream.start();
		} else {
			store.dispatch(updateFrames([]));
			store.dispatch(updateChats([]));
		}
	}

	// ** 4 **
	onTrackedUserIdsChanged(trackedUserIds: string[]) {
		this._trackedUserStream?.stop('onTrackedUserIdsChanged');

		if (trackedUserIds.length > 0) {
			this._trackedUserStream = new TrackedUserStream(trackedUserIds);
			this._trackedUserStream.start();
		} else {
			store.dispatch(updateTrackedUsers([]));
		}
	}
}

new ElectricSync();
